// src/pages/VirtualSwapPage.jsx
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchSwapItems,
  removeSwapItem,
  generateSwap,
  fetchAssets
} from '../store/reducers/virtualSwapReducer'
import { setCredits } from '../store/reducers/creditsSlice'
import { trackVirtualAction } from '../store/reducers/customerVirtualActivitySlice'
import { recordProductGenerate } from '../store/reducers/productVirtualActivitySlice'
import { useNavigate } from 'react-router-dom'
import { AiFillCloseCircle } from 'react-icons/ai'
import toast from 'react-hot-toast'
import axios from 'axios'
import { base_url } from '../utils/config'
import VirtualHeader from '../components/virtualHeader'

export default function VirtualSwapPage() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { token, userInfo } = useSelector(s => s.auth)
  const { items, count, loading, assets } = useSelector(s => s.virtualSwap)
  
  // Calculate actual count from items array to ensure accuracy
  const actualCount = items.length
  const credits = useSelector(s => s.credits.remaining)
  const userId = userInfo?.id

  const [selectedIdx, setSelectedIdx] = useState({})
  const [userImage, setUserImage] = useState(null)

  // placeholder intro images
  const beforeImgUrl = '/images/intro_before.jpg'
  const afterImgUrl = '/images/intro_after.jpg'

  // 1️⃣ redirect & load picks + assets, track visit
  useEffect(() => {
    if (!userId) return navigate('/login')
    dispatch(trackVirtualAction({
      userId,
      actionType: 'visit_swap_page'
    }))
    dispatch(fetchSwapItems())
    dispatch(fetchAssets())
  }, [dispatch, navigate, userId])

  // 2️⃣ init carousel indices
  useEffect(() => {
    const init = {}
    items.forEach(i => { init[i._id] = 0 })
    setSelectedIdx(init)
  }, [items])

  // 3️⃣ upload to backend → get Cloudinary URL, track upload
  const onFileChange = async e => {
    const file = e.target.files[0]
    if (!file) return
    const form = new FormData()
    form.append('file', file)
    try {
      const { data } = await axios.post(
        `${base_url}/api/virtual-swap/upload`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setUserImage(data.url)
      toast.success('Photo uploaded!')
      dispatch(trackVirtualAction({
        userId,
        actionType: 'upload_image'
      }))
    } catch (err) {
      console.error('Upload failed:', err.response?.data || err.message)
      toast.error('Upload failed: ' + (err.response?.data?.error || err.message))
      dispatch(trackVirtualAction({
        userId,
        actionType: 'upload_image_failed',
        metadata: { error: err.message }
      }))
    }
  }

  // remove a staged item, track remove
  const handleRemove = id => {
    dispatch(trackVirtualAction({
      userId,
      actionType: 'remove_swap_item',
      metadata: { itemId: id }
    }))
    dispatch(removeSwapItem(id))
      .unwrap()
      .then(() => toast.success('Removed'))
      .catch(() => toast.error('Removal failed'))
  }

  // block generate if invalid
  const canGenerate =
    !!userImage &&
    actualCount >= 3 &&
    actualCount <= 10 &&
    assets.length < 30 &&
    credits > 0

  // start generation, track start/success/failure
  const handleGenerate = () => {
    if (assets.length >= 30) {
      return toast.error('You have 30 saved assets already.')
    }
    if (credits <= 0) {
      return toast.error('You are out of credits. Wait for renewal.')
    }
    const picks = items.map(item => ({
      productId: item.productId,
      image: item.images?.[selectedIdx[item._id]] ?? item.image,
      category: item.category
    }))

    dispatch(trackVirtualAction({
      userId,
      actionType: 'generate_start',
      metadata: { numPicks: picks.length }
    }))

    dispatch(generateSwap({ userImage, picks }))
      .unwrap()
      .then(({ remaining }) => {
        dispatch(setCredits(remaining))
        dispatch(trackVirtualAction({
          userId,
          actionType: 'generate_success',
          metadata: { generated: picks.length }
        }))

        // ── Product-level: record a “generate” for each picked product ──
        picks.forEach(pick => {
          dispatch(recordProductGenerate({
            productId: pick.productId,
            creditsUsed: 1
          }))
        })

        navigate('/fashnassets')
      })
      .catch(err => {
        toast.error(err.error || 'Generation failed')
        dispatch(trackVirtualAction({
          userId,
          actionType: 'generate_failure',
          metadata: { error: err.error || 'unknown' }
        }))
      })
  }

  return (
    <div className="relative min-h-screen bg-gray-50 pb-32 overflow-x-hidden">
      <VirtualHeader />

      {/* Intro section */}
      <section className="mt-16 bg-blue-50 py-8">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Not sure about it? Try it on virtually!
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            See how products look on you before you buy.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <img src={beforeImgUrl} alt="Before Try-On" className="w-24 h-24 object-cover rounded-lg shadow" />
            <span className="text-2xl font-semibold text-gray-500">→</span>
            <img src={afterImgUrl} alt="After Try-On" className="w-24 h-24 object-cover rounded-lg shadow" />
          </div>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Upload card */}
        <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center">
          <label htmlFor="swap-upload" className="w-full h-56 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-500 transition">
            {userImage
              ? <img src={userImage} alt="You" className="h-full object-cover rounded-lg" />
              : <span className="text-gray-400">Upload your full‑body photo</span>
            }
          </label>
          <input id="swap-upload" type="file" accept="image/*" onChange={onFileChange} className="hidden" />
          <p className="mt-2 text-sm text-gray-500">Plain background · Well‑lit · Standing pose · Min 512×512px</p>
        </div>

        {/* Picks overview */}
        <div>
          <h2 className="text-xl font-semibold mb-4">
            Your Picks ({actualCount})
          </h2>
          {actualCount === 0 ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-800 font-medium">Get started!</p>
              <p className="text-blue-600 text-sm">Browse products and add 3-10 items to your virtual try-on collection.</p>
            </div>
          ) : actualCount < 3 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-800 font-medium">Add more products</p>
              <p className="text-yellow-600 text-sm">You need at least 3 products for virtual try-on. Currently: {actualCount}/3</p>
            </div>
          ) : actualCount > 10 ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 font-medium">Too many products</p>
              <p className="text-red-600 text-sm">Please remove some products. Maximum allowed: 10. Currently: {actualCount}/10</p>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-800 font-medium">Perfect!</p>
              <p className="text-green-600 text-sm">You have {actualCount} products ready for virtual try-on.</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {items.map(item => {
              const images = item.images || [item.image]
              const sel = selectedIdx[item._id] || 0
              const price = item.discount
                ? Math.floor(item.price * (1 - item.discount / 100))
                : item.price
              return (
                <div key={item._id} className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="relative">
                    <img src={images[sel]} alt={item.name} className="w-full h-48 object-cover" />
                    <button onClick={() => handleRemove(item._id)} className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full">
                      <AiFillCloseCircle />
                    </button>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold truncate">{item.name}</h3>
                    <p className="text-sm text-gray-500 truncate">{item.category} · {item.shopName}</p>
                    <p className="mt-2 text-green-600 font-bold">₹{price}</p>
                    <div className="mt-2 flex space-x-2 overflow-x-auto">
                      {images.map((img, i) => (
                        <img
                          key={i}
                          src={img}
                          alt="thumb"
                          className={`w-12 h-12 object-cover rounded cursor-pointer border-2 ${sel === i ? 'border-blue-600' : 'border-transparent'}`}
                          onClick={() => setSelectedIdx(prev => ({ ...prev, [item._id]: i }))}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Generate CTA */}
        <div className="text-center">
          <button
            onClick={handleGenerate}
            disabled={!canGenerate || loading}
            className="px-8 py-3 bg-green-600 text-white rounded-full font-semibold shadow hover:bg-green-700 disabled:opacity-50 transition"
          >
            {credits <= 0
              ? 'No credits left'
              : loading
                ? 'Generating…'
                : 'Start My Virtual Try-On'}
          </button>
        </div>
      </div>
    </div>
  )
}
